import { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ConversationSheet({ open, onOpenChange, ts, onUpdated, role }) {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  if (!ts) return null;
  const conv = ts.conversation || [];
  const isAdmin = user?.role === "admin";
  const isAccounts = user?.role === "accounts";

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post("/timesheet/message", { timesheet_id: ts.id, message: msg.trim() });
      setMsg("");
      onUpdated();
      toast.success("Message sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post("/timesheet/approve", { timesheet_id: ts.id, message: msg.trim() });
      toast.success("Approved");
      setMsg("");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const clarify = async () => {
    if (!msg.trim()) { toast.error("Please add a clarification message"); return; }
    setBusy(true);
    try {
      await api.post("/timesheet/clarify", { timesheet_id: ts.id, message: msg.trim() });
      toast.success("Clarification requested");
      setMsg("");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-card border-border text-foreground sm:max-w-lg w-full">
        <SheetHeader>
          <SheetTitle className="text-foreground font-display">Conversation · {ts.id}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-3 overflow-y-auto max-h-[55vh] pr-2" data-testid="conversation-list">
          {conv.length === 0 && <div className="text-sm text-muted-foreground p-4 border border-dashed border-border rounded-md text-center">No messages yet</div>}
          {conv.map(m => (
            <div key={m.id} className={`p-3 rounded-md border ${m.kind === "approve" ? "border-emerald-500/40 bg-emerald-500/[0.06]" : m.kind === "clarify" ? "border-amber-500/40 bg-amber-500/[0.06]" : m.kind === "submit" ? "border-blue-500/40 bg-blue-500/[0.06]" : "border-border bg-muted/30"}`}>
              <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground uppercase tracking-widest">
                <span>{m.from_name} · {m.from_role}</span>
                <span>{m.kind}</span>
              </div>
              <div className="text-sm mt-1 text-foreground whitespace-pre-wrap">{m.text}</div>
              <div className="text-[10px] text-muted-foreground font-mono mt-1">{new Date(m.at).toLocaleString()}</div>
            </div>
          ))}
        </div>

        {!isAccounts && (
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            <Textarea data-testid="conversation-message" placeholder="Write a message…" rows={3} value={msg} onChange={e => setMsg(e.target.value)} className="bg-background border-border" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={send} disabled={busy || !msg.trim()} variant="outline" data-testid="conversation-send"><Send className="h-4 w-4 mr-1.5" /> Send</Button>
              {isAdmin && ts.status === "submitted" && (
                <>
                  <Button onClick={approve} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-black" data-testid="conversation-approve"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</Button>
                  <Button onClick={clarify} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="conversation-clarify"><AlertCircle className="h-4 w-4 mr-1.5" /> Request Clarification</Button>
                </>
              )}
            </div>
          </div>
        )}
        {isAccounts && <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">Accounts users have read-only access</div>}
      </SheetContent>
    </Sheet>
  );
}
